import React, { useState, useEffect, useMemo, type ReactNode } from 'react';

import { DownloadOutlined, DownOutlined, EditOutlined, PrinterOutlined, QuestionCircleFilled, SettingOutlined, StopOutlined, WarningOutlined } from '@ant-design/icons';
import OperatorInboundFlowConceptModal from '@shared/inbounds/OperatorInboundFlowConceptModal';
import { ItemsSnapshotView, type ItemSnapshot } from '@shared/histories/ItemsSnapshotView';
import ListPageHeader from '@shared/ListPageHeader';
import Can from '@shared/permissions/Can';
import CanShow from '@shared/permissions/CanShow';
import useCan from '@shared/permissions/useCan';
import TableIndex from '@shared/table/TableIndex';
import ValueAddedServicesCard from '@shared/ValueAddedServicesCard';
import { handleApiError, handleNetworkError } from '@utils/apiErrorHandler';
import {
  Badge,
  Card,
  Collapse,
  Descriptions,
  Dropdown,
  List,
  Table,
  Tag,
  Button,
  Space,
  Tabs,
  Tooltip,
  Modal,
  message,
  Empty,
  Timeline,
  Alert
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link, useRevalidator } from 'react-router-dom';
import { RECEIPT_PUTAWAYABLE_STATUSES } from '@constants/receipts';

import { useBackgroundJob } from '@shared/BackgroundJobManager';
import { formatDateTime, formatDate } from '../../../utils/dateTime';
import { CustomAttributesTags } from '@components/shared/inventory_attribute_definitions/CustomAttributesTags';
import { receiptService } from '../../../services/operator/receiptService';
import adminInventoryAttributeDefinitionService from '@services/admin/inventoryAttributeDefinitionService';
import operatorInventoryAttributeDefinitionService from '@services/operator/inventoryAttributeDefinitionService';

interface InboundShowProps {
  inbound: any;
  // useInboundService 是 .js hook，返回的 service 物件方法多樣（cancel/resume/generatePdf/
  // generateLabels/getShelfMovements/getSerialNumbers/addValueAddedService/...），
  // 超過 4 個多態方法，依 do-ts skill 指引 fall back 為 any。
  useInboundService: () => any;
  thirdPartyApiLogService?: any;
  // 用於搜尋加值服務範本
  valueAddedServiceService?: any;
  // 角色 (例如：'admin' 或 'operator')
  role?: string;
}

interface ReceiptItemRow {
  id: string;
  product_id?: string;
  product_sku?: string;
  product_name?: string;
  inventory_type_name?: string;
  batch?: string;
  manufacturing_date?: string;
  expiration_date?: string;
  custom_attributes?: Record<string, unknown> | null;
  quantity?: number;
  putaway_quantity?: number;
  verifier_name?: string;
  received_at?: string;
  note?: string;
  unplanned?: boolean;
  inbound_item_id?: string;
  [key: string]: unknown;
}

interface InboundItemRow {
  id: string;
  product_id?: string;
  product_sku?: string;
  product_name?: string;
  inventory_type_name?: string;
  manufacturing_date?: string;
  expiration_date?: string;
  batch?: string;
  custom_attributes?: Record<string, unknown> | null;
  scheduled_quantity?: number;
  received_quantity?: number;
  putaway_quantity?: number;
  unplanned?: boolean;
  receipt_items?: ReceiptItemRow[];
  [key: string]: unknown;
}

const InboundShow: React.FC<InboundShowProps> = ({
  inbound: initialInbound,
  useInboundService,
  thirdPartyApiLogService,
  valueAddedServiceService,
  role = 'admin'
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  const can = useCan();
  const [inbound, setInbound] = useState<any>(initialInbound);
  const service = useInboundService();
  // useBackgroundJob 是 .js hook，TS 推為 Object（非 destructurable）
  const { addJob } = useBackgroundJob() as { addJob: (jobId: string, name: string) => void };

  // handleApiError / handleNetworkError 是 .js util，宣告 signature 比實際嚴格
  // (要求 onError + Error)；保留原 runtime 用法、cast 為 any 來放行。
  const apiError = handleApiError as any;
  const networkError = handleNetworkError as any;

  // OperatorInboundFlowConceptModal 是 .jsx 子元件，TS 推導的 props 不完整，
  // 以 ComponentType<any> 包一層；放在 component body 內以避免 Vite HMR 下的 top-level TDZ。
  const FlowConceptModal = OperatorInboundFlowConceptModal as React.ComponentType<any>;

  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
  const [apiLogs, setApiLogs] = useState<any[] | null>(null);
  const [flowConceptVisible, setFlowConceptVisible] = useState(false);
  const [apiLogsLoading, setApiLogsLoading] = useState(false);
  const [putawayPickerOpen, setPutawayPickerOpen] = useState(false);
  const [attributeDefinitions, setAttributeDefinitions] = useState<Array<{ name: string; label: string }>>([]);

  useEffect(() => {
    const fetchDefinitions = async () => {
      try {
        let result: any;
        if (role === 'admin') {
          result = await (adminInventoryAttributeDefinitionService as any).fetchList();
        } else if (role === 'operator' && inbound?.merchant_id) {
          result = await (operatorInventoryAttributeDefinitionService as any).fetchList({ merchant_id: inbound.merchant_id });
        }
        setAttributeDefinitions(result?.data || []);
      } catch (error) {
        console.error('Failed to fetch inventory attribute definitions', error);
        setAttributeDefinitions([]);
      }
    };
    fetchDefinitions();
  }, [role, inbound?.merchant_id]);

  const attributeLabelByName = useMemo(
    () => Object.fromEntries(attributeDefinitions.map((def) => [def.name, def.label])),
    [attributeDefinitions]
  );

  const loadApiLogs = async () => {
    if (!thirdPartyApiLogService || apiLogsLoading) return;
    setApiLogsLoading(true);
    try {
      const res = await thirdPartyApiLogService.getList({ loggable_type: 'Inbound', loggable_id: inbound.id });
      setApiLogs(res.data || []);
    } catch (e) {
      message.error(t('common.error_label') as string);
    } finally {
      setApiLogsLoading(false);
    }
  };

  // Use revalidator.state for loading indication
  const isRevalidating = revalidator.state === 'loading';

  // Sync loader data changes
  useEffect(() => {
    if (initialInbound) {
      setInbound(initialInbound);
      console.log('[InboundShow] return_orders:', initialInbound.return_orders);
      console.log('[InboundShow] inbound_type:', initialInbound.inbound_type);
    }
  }, [initialInbound]);

  // Auto-expand all rows when receipts ≤ 5
  useEffect(() => {
    if (inbound?.receipts) {
      const expandableReceipts = inbound.receipts.filter((receipt: any) => receipt.receipt_items?.length > 0);
      if (expandableReceipts.length > 0 && expandableReceipts.length <= 5) {
        setExpandedRowKeys(expandableReceipts.map((receipt: any) => receipt.id));
      }
    }
  }, [inbound?.receipts]);

  // Check if a receipt item has mismatched batch/expiry compared to the plan
  const hasMismatch = (planItem: any, receiptItem: any) => {
    if (planItem.batch && receiptItem.batch && planItem.batch !== receiptItem.batch) return true;
    if (planItem.expiration_date && receiptItem.expiration_date && planItem.expiration_date !== receiptItem.expiration_date) return true;
    return false;
  };

  // Check if any receipt_item in the record has mismatch
  const hasAnyMismatch = (record: any) => {
    return record.receipt_items?.some((ri: any) => hasMismatch(record, ri)) || false;
  };
  const handleCancel = async () => {
    try {
      const response = await service.cancel(inbound.id);
      if (response.success) {
        message.success(t('common.submit.success') as string);
        revalidator.revalidate(); // Trigger loader to reload data
      } else {
        apiError(response, { t, fallbackMessage: t('common.error.operation_failed') });
      }
    } catch (error) {
      networkError(error, { t });
      console.error('Cancel error:', error);
    }
  };
  const handleResume = async () => {
    try {
      const response = await service.resume(inbound.id);
      if (response.success) {
        message.success(t('common.submit.success') as string);
        revalidator.revalidate(); // Trigger loader to reload data
      } else {
        apiError(response, { t, fallbackMessage: t('common.error.operation_failed') });
      }
    } catch (error) {
      networkError(error, { t });
      console.error('Resume error:', error);
    }
  };
  const handleDownloadPdf = async () => {
    try {
      const response = await service.generatePdf(inbound.id);
      if (response.success) {
        addJob(response.data.job_id, 'common.button.download_pdf');
      } else {
        apiError(response, { t, fallbackMessage: t('common.error.operation_failed') });
      }
    } catch (error) {
      networkError(error, { t });
    }
  };

  const handleDownloadLabels = async () => {
    try {
      const response = await service.generateLabels(inbound.id);
      if (response.success) {
        addJob(response.data.job_id, 'common.button.download_labels');
      } else {
        apiError(response, { t, fallbackMessage: t('common.error.operation_failed') });
      }
    } catch (error) {
      networkError(error, { t });
    }
  };

  const handleAutoPutaway = async (receiptId: string) => {
    try {
      const response = await receiptService.autoPutaway(receiptId);
      if (response.success) {
        message.success(t('operator.pages.receipts.auto_putaway_success') as string);
        revalidator.revalidate();
      } else {
        apiError(response, { t, fallbackMessage: t('operator.pages.receipts.auto_putaway_failed') });
      }
    } catch (error) {
      networkError(error, { t });
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      pending: 'blue',
      verified: 'cyan',
      putting_away: 'orange',
      done: 'green',
      canceled: 'red'
    };
    return colors[status] || 'default';
  };
  const getInboundTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      receiving: 'blue',
      sales_return: 'orange',
      production: 'green'
    };
    return colors[type] || 'default';
  };
  // Items table columns
  const itemColumns = [
    {
      title: t(`models.inbound_item.product_sku`),
      dataIndex: 'product_sku',
      key: 'product_sku',
      render: (sku: string, record: InboundItemRow) => (
        <Space size={4}>
          <Link to={`/products/${record.product_id}`} style={{ color: '#1890ff' }}>
            {sku}
          </Link>
          {record.unplanned && (
            <Tag color="orange">{t('models.inbound_item.unplanned')}</Tag>
          )}
        </Space>
      ),
    },
    {
      title: t(`models.inbound_item.product_name`),
      dataIndex: 'product_name',
      key: 'product_name',
    },
    {
      title: t(`models.inbound_item.inventory_type`),
      dataIndex: 'inventory_type_name',
      key: 'inventory_type_name',
      render: (text: string) => text || '-'
    },
    {
      title: t(`models.inbound_item.manufacturing_date`),
      dataIndex: 'manufacturing_date',
      key: 'manufacturing_date',
      render: (date: string) => date ? formatDate(date) : '-'
    },
    {
      title: t(`models.inbound_item.expiration_date`),
      dataIndex: 'expiration_date',
      key: 'expiration_date',
      render: (date: string) => date ? formatDate(date) : '-'
    },
    {
      title: t(`models.inbound_item.batch`),
      dataIndex: 'batch',
      key: 'batch',
      render: (text: string) => text || '-'
    },
    {
      title: t(`models.inventory.custom_attributes`),
      dataIndex: 'custom_attributes',
      key: 'custom_attributes',
      render: (custom_attributes: Record<string, unknown> | null | undefined) => (
        <CustomAttributesTags
          attributes={custom_attributes}
          attributeLabelByName={attributeLabelByName}
        />
      )
    },
    {
      title: t(`models.inbound_item.scheduled_quantity`),
      dataIndex: 'scheduled_quantity',
      key: 'scheduled_quantity',
      render: (qty: number, record: InboundItemRow) => record.unplanned ? '-' : (qty || 0)
    },
    {
      title: t(`models.inbound_item.received_quantity`),
      dataIndex: 'received_quantity',
      key: 'received_quantity',
      render: (qty: number, record: InboundItemRow) => {
        const verified = qty || 0;
        const scheduled = record.scheduled_quantity || 0;
        const isUnplanned = record.unplanned;
        const percentage = scheduled > 0 ? ((verified / scheduled) * 100).toFixed(0) : 0;
        let color = '#999';
        if (verified === 0) color = '#999';
        else if (isUnplanned) color = '#1890ff';
        else if (verified >= scheduled) color = '#52c41a';
        else if (verified >= scheduled * 0.5) color = '#faad14';
        else color = '#ff4d4f';
        const receiptCount = record.receipt_items?.length || 0;
        return (
          <Space size={4}>
            <span style={{ color }}>{verified}{!isUnplanned && ` (${percentage}%)`}</span>
            {receiptCount > 1 && (
              <Badge count={t('models.receipt_item.batch_count', { count: receiptCount })} style={{ backgroundColor: '#1890ff' }} />
            )}
            {hasAnyMismatch(record) && (
              <Tooltip title={t('models.receipt_item.mismatch')}>
                <WarningOutlined style={{ color: '#faad14' }} />
              </Tooltip>
            )}
          </Space>
        );
      }
    },
    {
      title: t(`models.inbound_item.putaway_quantity`),
      dataIndex: 'putaway_quantity',
      key: 'putaway_quantity',
      render: (qty: number, record: InboundItemRow) => {
        const putaway = qty || 0;
        const scheduled = record.scheduled_quantity || 0;
        const isUnplanned = record.unplanned;
        const percentage = scheduled > 0 ? ((putaway / scheduled) * 100).toFixed(0) : 0;
        let color = '#999';
        if (putaway === 0) color = '#999';
        else if (isUnplanned) color = '#1890ff';
        else if (putaway >= scheduled) color = '#52c41a';
        else if (putaway >= scheduled * 0.5) color = '#faad14';
        else color = '#ff4d4f';
        return <span style={{ color }}>{putaway}{!isUnplanned && ` (${percentage}%)`}</span>;
      }
    },
  ];
  // Shelf Histories table columns (上架紀錄/儲位紀錄) for TableIndex
  const ShelfMovementColumns = useMemo(() => [
    {
      nameKey: `common.created_at`,
      persist: true,
      width: '180px',
      selector: ({ created_at }: any) => created_at,
    },
    {
      nameKey: `models.product._`,
      width: '250px',
      wrap: true,
      disableTooltip: true,
      cell: ({ product_id, product_sku, product_name }: any) => product_sku ? (
        <Link to={`/products/${product_id}`} style={{ color: '#1890ff' }}>
          {product_sku} - {product_name}
        </Link>
      ) : '-',
    },
    {
      nameKey: `models.inbound_item.inventory_type`,
      width: '120px',
      selector: ({ inventory_type_name }: any) => inventory_type_name || '-',
    },
    {
      nameKey: `models.inbound_item.batch`,
      width: '120px',
      selector: ({ batch }: any) => batch || '-',
    },
    {
      nameKey: `models.inbound_item.expiration_date`,
      width: '120px',
      selector: ({ expiration_date }: any) => expiration_date ? formatDate(expiration_date) : '-',
    },
    {
      nameKey: `models.shelf_movement.to_shelf`,
      width: '150px',
      selector: ({ to_shelf_location }: any) => to_shelf_location || '-',
    },
    {
      nameKey: `models.shelf_movement.quantity`,
      width: '100px',
      selector: ({ quantity }: any) => quantity || 0,
    },
    {
      nameKey: `models.shelf_movement.operator`,
      width: '150px',
      selector: ({ operator_name }: any) => operator_name || '-',
    },
  ], [t]);
  // Value Added Services table columns
  const valueAddedServiceColumns = [
    {
      title: t(`models.value_added_service.title`),
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: t(`models.value_added_service.quantity`),
      dataIndex: 'quantity',
      key: 'quantity',
      render: (qty: number) => qty || 0
    },
    {
      title: t(`models.value_added_service.price`),
      dataIndex: 'price',
      key: 'price',
      render: (price: string | number | null | undefined) => price ? `$${parseFloat(String(price)).toFixed(2)}` : '-'
    },
    {
      title: t(`common.created_at`),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => formatDateTime(date)
    },
  ];
  // Serial Numbers table columns for TableIndex
  const serialNumberColumns = useMemo(() => [
    {
      nameKey: `models.serial_number.number`,
      persist: true,
      width: '150px',
      selector: ({ number }: any) => number,
    },
    {
      nameKey: `models.serial_number.product_sku`,
      width: '150px',
      selector: ({ product_sku }: any) => product_sku || '-',
    },
    {
      nameKey: `models.serial_number.product_name`,
      width: '200px',
      wrap: true,
      selector: ({ product_name }: any) => product_name || '-',
    },
    {
      nameKey: `common.created_at`,
      width: '180px',
      selector: ({ created_at }: any) => created_at,
    },
  ], [t]);
  const receiptItemWithProductColumns = [
    {
      title: t(`models.inbound_item.product_sku`),
      dataIndex: 'product_sku',
      key: 'product_sku',
      render: (sku: string, ri: ReceiptItemRow) => (
        <Space size={4}>
          <Link to={`/products/${ri.product_id}`} style={{ color: '#1890ff' }}>
            {sku}
          </Link>
          {ri.unplanned && (
            <Tag color="orange">{t('models.inbound_item.unplanned')}</Tag>
          )}
        </Space>
      ),
    },
    {
      title: t(`models.inbound_item.product_name`),
      dataIndex: 'product_name',
      key: 'product_name',
    },
    {
      title: t(`models.inbound_item.inventory_type`),
      dataIndex: 'inventory_type_name',
      key: 'inventory_type_name',
      render: (text: string) => text || '-'
    },
    {
      title: t('models.receipt_item.batch'),
      dataIndex: 'batch',
      key: 'batch',
      render: (text: string, ri: ReceiptItemRow) => {
        const planItem = inbound?.items?.find((item: any) => item.id === ri.inbound_item_id);
        const isMismatch = planItem?.batch && text && planItem.batch !== text;
        return <span style={isMismatch ? { color: '#ff4d4f', fontWeight: 'bold' } : {}}>{text || '-'}</span>;
      }
    },
    {
      title: t('models.receipt_item.manufacturing_date'),
      dataIndex: 'manufacturing_date',
      key: 'manufacturing_date',
      render: (date: string, ri: ReceiptItemRow) => {
        const planItem = inbound?.items?.find((item: any) => item.id === ri.inbound_item_id);
        const isMismatch = planItem?.manufacturing_date && date && planItem.manufacturing_date !== date;
        return <span style={isMismatch ? { color: '#ff4d4f', fontWeight: 'bold' } : {}}>{date ? formatDate(date) : '-'}</span>;
      }
    },
    {
      title: t('models.receipt_item.expiration_date'),
      dataIndex: 'expiration_date',
      key: 'expiration_date',
      render: (date: string, ri: ReceiptItemRow) => {
        const planItem = inbound?.items?.find((item: any) => item.id === ri.inbound_item_id);
        const isMismatch = planItem?.expiration_date && date && planItem.expiration_date !== date;
        return <span style={isMismatch ? { color: '#ff4d4f', fontWeight: 'bold' } : {}}>{date ? formatDate(date) : '-'}</span>;
      }
    },
    {
      title: t('models.receipt_item.custom_attributes'),
      dataIndex: 'custom_attributes',
      key: 'custom_attributes',
      render: (custom_attributes: Record<string, unknown> | null | undefined) => (
        <CustomAttributesTags
          attributes={custom_attributes}
          attributeLabelByName={attributeLabelByName}
        />
      )
    },
    {
      title: t('models.receipt_item.quantity'),
      dataIndex: 'quantity',
      key: 'quantity',
      render: (qty: number) => qty || 0,
    },
    {
      title: t('models.receipt_item.putaway_quantity'),
      dataIndex: 'putaway_quantity',
      key: 'putaway_quantity',
      render: (qty: number, ri: ReceiptItemRow) => {
        const received = ri.quantity || 0;
        const putaway = qty || 0;
        let color = '#999';
        if (putaway === 0) color = '#999';
        else if (putaway >= received) color = '#52c41a';
        else color = '#faad14';
        return <span style={{ color }}>{putaway}</span>;
      }
    },
    {
      title: t('models.receipt_item.verifier_name'),
      dataIndex: 'verifier_name',
      key: 'verifier_name',
      render: (text: string) => text || '-',
    },
    {
      title: t('models.receipt_item.received_at'),
      dataIndex: 'received_at',
      key: 'received_at',
      render: (date: string) => date ? formatDateTime(date) : '-',
    },
    {
      title: t('models.receipt_item.note'),
      dataIndex: 'note',
      key: 'note',
      render: (text: string) => text || '-',
    },
  ];

  if (!inbound) {
    return (
      <Empty description={t('common.error.record_not_found')} />
    );
  }
  // ====== Receipts Section (收貨明細) ======
  const receiptStatusColors: Record<string, string> = {
    receiving: 'processing',
    pending_putaway: 'warning',
    putting_away: 'cyan',
    done: 'success',
    canceled: 'error'
  };

  // ====== Secondary Tabs (次要資訊) ======
  const secondaryTabItems = [
    ...(role === 'operator' ? [{
      key: 'shelf_movements',
      label: t(`models.inbound.tabs.shelf_movements`),
      children: (
        <TableIndex
          columns={ShelfMovementColumns}
          service={{
            getRecords: (params: Record<string, unknown>) => service.getShelfMovements(inbound.id, params),
          }}
          searchable={true}
          columnsEditable={false}
          tableId={`${role}InboundShelfMovementsTable`}
          defaultQuery={{
            sorting_field: 'shelf_movements.id',
            sorting_direction: 'desc',
          }}
          supportRules={[]}
        />
      ),
    }] : []),
    {
      key: 'serial_numbers',
      label: t(`models.inbound.tabs.serial_numbers`),
      children: (
        <TableIndex
          columns={serialNumberColumns}
          service={{
            getRecords: (params: Record<string, unknown>) => service.getSerialNumbers(inbound.id, params),
          }}
          searchable={true}
          columnsEditable={false}
          tableId={`${role}InboundSerialNumbersTable`}
          defaultQuery={{
            sorting_field: 'serial_numbers.created_at',
            sorting_direction: 'desc',
          }}
          supportRules={[]}
        />
      ),
    },
  ];
  // 檢查按鈕權限（使用 API 返回的權限標誌）
  const isEditable = inbound?.editable === true;
  const isCancelable = inbound?.cancelable === true;
  const isResumable = inbound?.resumable === true;
  const basePath = role === 'operator' ? '/operator' : '/admin';

  // 1. 主操作（裸按鈕，當前最需要做的事）
  const primaryActions: ReactNode[] = [];

  if (role === 'operator' && inbound.status === 'pending') {
    primaryActions.push(
      <Button
        key="acceptance"
        type="primary"
        onClick={() => navigate(`acceptance`)}
        style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
      >
        {t('common.button.verify_items', { defaultValue: '入庫驗收' })}
      </Button>
    );
  }

  // 可上架的收貨單（手動上架作業）— 僅 Dynamic 倉庫需要手動配架；Virtual/Dedicated 走 auto_putaway。
  const isDynamicWarehouse = inbound.warehouse?.storage_mode === 'dynamic';
  const putawayReceipts = isDynamicWarehouse
    ? (inbound.receipts || []).filter((r: any) => RECEIPT_PUTAWAYABLE_STATUSES.includes(r.status))
    : [];

  if (role === 'operator' && putawayReceipts.length > 0) {
    primaryActions.push(
      <Button
        key="manual_putaway"
        type="primary"
        onClick={() => {
          if (putawayReceipts.length === 1) {
            navigate(`/receipts/${putawayReceipts[0].id}/putaway`);
          } else {
            setPutawayPickerOpen(true);
          }
        }}
      >
        {t('operator.pages.receipts.manual_putaway')}
      </Button>
    );
  }

  if (isEditable) {
    primaryActions.push(
      <Can key="edit" requiredPermission="inbounds:write">
        <Button
          type="primary"
          icon={<EditOutlined />}
          onClick={() => navigate(`edit`)}
        >
          {t('common.button.edit')}
        </Button>
      </Can>
    );
  }

  // 2. 列印（輸出相關）
  const printMenuItems = [
    {
      key: 'pdf',
      label: t('common.button.download_pdf'),
      icon: <DownloadOutlined />,
      requiredPermission: 'inbounds:read',
      onClick: handleDownloadPdf,
    },
    {
      key: 'labels',
      label: t('common.button.download_labels'),
      icon: <DownloadOutlined />,
      requiredPermission: 'inbounds:read',
      onClick: handleDownloadLabels,
    },
  ].filter(item => !item.requiredPermission || can(item.requiredPermission as any));

  // 3. 更多操作（日常操作）
  const moreActions: any[] = [];

  if (isResumable) {
    moreActions.push({
      key: 'resume',
      label: t('common.button.resume'),
      requiredPermission: 'inbounds:write',
      onClick: () => {
        Modal.confirm({
          title: t('shared.inbounds.show.resume_confirm_title'),
          content: t('shared.inbounds.show.resume_confirm_content', { name: inbound.name }),
          okText: t('common.button.confirm'),
          cancelText: t('common.button.cancel'),
          onOk: handleResume,
        });
      },
      disabled: isRevalidating,
    });
  }

  const visibleMoreActions = moreActions.filter(
    item => !item.requiredPermission || can(item.requiredPermission)
  );

  // 4. 異常管制（破壞性操作）
  const dangerActions: any[] = [];

  if (isCancelable) {
    dangerActions.push({
      key: 'cancel',
      label: t('common.button.cancel'),
      danger: true,
      requiredPermission: 'inbounds:write',
      onClick: () => {
        Modal.confirm({
          title: t('shared.inbounds.show.cancel_confirm_title'),
          content: t('shared.inbounds.show.cancel_confirm_content', { name: inbound.name }),
          okText: t('common.button.confirm'),
          cancelText: t('common.button.cancel'),
          okButtonProps: { danger: true },
          onOk: handleCancel,
        });
      },
      disabled: isRevalidating,
    });
  }

  const visibleDangerActions = dangerActions.filter(
    item => !item.requiredPermission || can(item.requiredPermission)
  );

  const headerActions = (
    <>
      {primaryActions}

      {printMenuItems.length > 0 && (
        <Dropdown menu={{ items: printMenuItems }} trigger={['click']}>
          <Button icon={<PrinterOutlined />}>
            {t('common.button.print')} <DownOutlined />
          </Button>
        </Dropdown>
      )}

      {visibleMoreActions.length > 0 && (
        <Dropdown menu={{ items: visibleMoreActions }} trigger={['click']}>
          <Button icon={<SettingOutlined />}>
            {t('common.more_actions', { defaultValue: '更多操作' })} <DownOutlined />
          </Button>
        </Dropdown>
      )}

      {visibleDangerActions.length > 0 && (
        <Dropdown menu={{ items: visibleDangerActions }} trigger={['click']}>
          <Button icon={<StopOutlined />} danger>
            {t('shared.orders.detail.sections.exceptions', { defaultValue: '異常管制' })} <DownOutlined />
          </Button>
        </Dropdown>
      )}
    </>
  );

  return (
    <div>
      <ListPageHeader
        basePath={basePath}
        items={['inbounds', 'inbounds.list', { label: inbound.name }]}
        actions={headerActions}
        guideButton={role === 'operator' ? (
          <Tooltip title={t('concepts.inbound_flow_operator.tooltip')}>
            <Button
              type="text"
              size="small"
              icon={<QuestionCircleFilled style={{ color: '#1890ff' }} />}
              onClick={() => setFlowConceptVisible(true)}
              style={{ padding: 0, height: 'auto', minWidth: 'auto' }}
            />
          </Tooltip>
        ) : null}
      />
      {role === 'operator' && (
        <FlowConceptModal
          visible={flowConceptVisible}
          onClose={() => setFlowConceptVisible(false)}
          currentStatus={inbound.status}
        />
      )}
      {role === 'operator' && inbound.status === 'pending' && (
        <Alert
          message={t('operator.inbound_acceptance.ready_title', { defaultValue: '此入庫單等待驗收中' })}
          description={
            <div style={{ marginTop: 8 }}>
              <Button type="primary" size="large" onClick={() => navigate(`acceptance`)}>
                {t('common.button.start_acceptance', { defaultValue: '開始驗收' })}
              </Button>
            </div>
          }
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}
      <Descriptions bordered column={2} size="small" style={{ marginBottom: 16 }}>
          <Descriptions.Item label={t(`models.inbound.name`)}>
            {inbound.name}
          </Descriptions.Item>
          <Descriptions.Item label={t(`models.inbound.barcode`)}>
            {inbound.barcode || '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t(`models.inbound.status`)}>
            <Tag color={getStatusColor(inbound.status)}>
              {t(`enums.inbound.status.${inbound.status}`)}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label={t(`models.inbound.inbound_type`)}>
            <Tag color={getInboundTypeColor(inbound.inbound_type)}>
              {t(`enums.inbound.type.${inbound.inbound_type}`)}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label={t(`models.inbound.eta`)}>
            {inbound.eta ? formatDate(inbound.eta) : '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('models.inbound.supplier')}>
            {inbound.supplier?.name || '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t(`models.inbound.extra_name`)}>
            {inbound.extra_name || '-'}
          </Descriptions.Item>
          {role !== 'operator' && (
            <Descriptions.Item label={t(`models.inbound.warehouse`)}>
              {inbound.warehouse_name}
            </Descriptions.Item>
          )}
          {role === 'operator' && (
            <Descriptions.Item label={t(`models.inbound.merchant`)}>
              {inbound.merchant_name || '-'}
            </Descriptions.Item>
          )}
          <Descriptions.Item label={t(`models.inbound.arrived_at`)}>
            {inbound.arrived_at ? formatDateTime(inbound.arrived_at) : '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t(`models.inbound.received_at`)}>
            {inbound.received_at ? formatDateTime(inbound.received_at) : '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t(`models.inbound.putaway_at`)}>
            {inbound.putaway_at ? formatDateTime(inbound.putaway_at) : '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t(`common.created_at`)}>
            {formatDateTime(inbound.created_at)}
          </Descriptions.Item>
          <Descriptions.Item label={t(`models.inbound.note`)} span={2}>
            {inbound.note || '-'}
          </Descriptions.Item>
          {inbound.inbound_type === 'sales_return' && (
            <Descriptions.Item label={t(`models.inbound.return_orders`, { defaultValue: '關聯退貨單' })} span={2}>
              {inbound.return_orders && inbound.return_orders.length > 0 ? (
                <Space direction="vertical" size={4}>
                  {inbound.return_orders.map((ro: any) => (
                    <Link key={ro.id} to={`/return_orders/${ro.id}`}>
                      {ro.name}
                    </Link>
                  ))}
                </Space>
              ) : '-'}
            </Descriptions.Item>
          )}
        </Descriptions>
      {/* Histories & API Logs */}
      <Card style={{ marginBottom: 16 }} size="small">
        <Tabs
          defaultActiveKey="histories"
          size="small"
          items={[
            {
              key: 'histories',
              label: t('models.inbound.histories'),
              children: inbound.histories && inbound.histories.length > 0 ? (
                <Timeline
                  mode="left"
                  items={inbound.histories.map((h: any) => ({
                    key: h.id,
                    children: (
                      <div>
                        <div>
                          {h.description ? (
                            (() => {
                              let parsed: any = h.description;
                              if (typeof h.description === 'string') {
                                try { parsed = JSON.parse(h.description); } catch (e) { /* not JSON */ }
                              }
                              if (parsed && typeof parsed === 'object' && parsed.key) {
                                const { key, ...params } = parsed;
                                if (key === 'edit_inbound_items' && (Array.isArray(params.before_items) || Array.isArray(params.after_items))) {
                                  return (
                                    <div>
                                      <strong>{t(`enums.inbound_history.action.${key}`, { defaultValue: key })}</strong>
                                      <ItemsSnapshotView
                                        beforeItems={(params.before_items as ItemSnapshot[]) ?? []}
                                        afterItems={(params.after_items as ItemSnapshot[]) ?? []}
                                        t={t}
                                        fieldKeyPrefix="models.inbound_item"
                                      />
                                    </div>
                                  );
                                }
                                if (params.error_code) {
                                  const interpol = params.interpolations || {};
                                  return (
                                    <div>
                                      <strong style={{ color: '#ff4d4f' }}>
                                        {t(`enums.inbound_history.action.${key}`, { ...params, defaultValue: key }) as ReactNode}
                                      </strong>
                                      <div style={{ marginTop: 4, fontSize: 12, color: '#a8071a', backgroundColor: '#fff2f0', padding: '4px 8px', borderRadius: 4, border: '1px solid #ffccc7' }}>
                                        {t([`enums.inbound_history.${key}.${params.error_code}`, `errors.${params.error_code}`], { ...interpol, defaultValue: params.error_code }) as ReactNode}
                                      </div>
                                    </div>
                                  );
                                }
                                // EC stock push 失敗用結構化 failures[] 攜帶 per-variant error_code +
                                // interpolations（BACKEND.md §6.4）。逐條走 i18n，避免回退到英文 raw error 字串。
                                if (Array.isArray(params.failures) && params.failures.length > 0) {
                                  return (
                                    <div>
                                      <strong style={{ color: '#ff4d4f' }}>
                                        {t(`enums.inbound_history.action.${key}`, { ...params, defaultValue: key }) as ReactNode}
                                      </strong>
                                      <div style={{ marginTop: 4, fontSize: 12, color: '#a8071a', backgroundColor: '#fff2f0', padding: '4px 8px', borderRadius: 4, border: '1px solid #ffccc7' }}>
                                        {params.failures.map((f: any, idx: number) => {
                                          const ip = f.interpolations || {};
                                          const label = [f.ec_item_code, f.ec_variant_code].filter(Boolean).join('/') || f.sku || '-';
                                          return (
                                            <div key={idx} style={{ marginBottom: idx === params.failures.length - 1 ? 0 : 2 }}>
                                              <span style={{ fontWeight: 500 }}>{label}</span>
                                              {f.delta != null && <span style={{ marginLeft: 4, color: '#666' }}>(Δ{f.delta})</span>}
                                              <span style={{ marginLeft: 6 }}>
                                                {t([`enums.inbound_history.${key}.${f.error_code}`, `errors.${f.error_code}`], { ...ip, defaultValue: f.error_code }) as ReactNode}
                                              </span>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  );
                                }
                                // 點收完成後修正數量：previous_quantity 存在時改畫差量
                                // 「50 → 55 (+5)」，沿用下方 fields_changed 的紅刪綠增風格，
                                // 讓操作員一眼看出是「修改」而非另一筆獨立點收。
                                if (key === 'receive_item' && params.previous_quantity != null) {
                                  const prev = Number(params.previous_quantity);
                                  const next = Number(params.quantity);
                                  const diff = next - prev;
                                  return (
                                    <div>
                                      <strong>{t('enums.inbound_history.action.receive_item_modified', { ...params, defaultValue: 'receive_item_modified' }) as ReactNode}</strong>
                                      <div style={{ marginTop: 4, fontSize: 12 }}>
                                        <span style={{ color: '#ff4d4f', textDecoration: 'line-through' }}>{prev}</span>
                                        <span style={{ margin: '0 8px' }}>→</span>
                                        <span style={{ color: '#52c41a' }}>{next}</span>
                                        <span style={{ marginLeft: 8, color: '#666' }}>({diff > 0 ? '+' : ''}{diff})</span>
                                      </div>
                                    </div>
                                  );
                                }
                                return <span>{t(`enums.inbound_history.action.${key}`, { ...params, defaultValue: key }) as ReactNode}</span>;
                              }
                              const descText = typeof h.description === 'string' ? h.description : (parsed?.text || '-');
                              return <span>{descText}</span>;
                            })()
                          ) : h.action ? (
                            <span>{t(`enums.inbound_history.action.${h.action}`, { defaultValue: h.action })}</span>
                          ) : (
                            <span>-</span>
                          )}
                        </div>
                        {h.description?.fields_changed && (
                          <div style={{ fontSize: '12px', color: '#1890ff', marginTop: '8px', padding: '8px', backgroundColor: '#f0f5ff', borderRadius: '4px' }}>
                            <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{t('models.history.fields_changed_label')}:</div>
                            {Object.entries(h.description.fields_changed).map(([field, valuePair]) => {
                              const [oldValue, newValue] = valuePair as [unknown, unknown];
                              return (
                                <div key={field} style={{ marginBottom: '4px', paddingLeft: '8px' }}>
                                  <strong>{field}</strong>:
                                  <span style={{ color: '#ff4d4f', textDecoration: 'line-through', marginLeft: '8px' }}>{oldValue === null || oldValue === '' ? '(空)' : String(oldValue)}</span>
                                  <span style={{ margin: '0 8px' }}>→</span>
                                  <span style={{ color: '#52c41a' }}>{newValue === null || newValue === '' ? '(空)' : String(newValue)}</span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        {(h.operator_name || h.user_name) && (
                          <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                            {h.operator_name
                              ? <>{t('models.history.by_operator')}: {h.operator_name}</>
                              : <>{t('models.history.by_user')}: {h.user_name}</>}
                          </div>
                        )}
                        <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>{formatDateTime(h.created_at)}</div>
                      </div>
                    )
                  }))}
                />
              ) : (
                <Empty description={t('common.table.no_data')} />
              ),
            },
            {
              key: 'api_logs',
              label: t('models.inbound.tabs.logs'),
              children: (() => {
                if (!apiLogs) {
                  return (
                    <div style={{ textAlign: 'center', padding: '24px' }}>
                      <Button onClick={loadApiLogs} loading={apiLogsLoading}>
                        {t('shared.orders.detail.logs.load')}
                      </Button>
                    </div>
                  );
                }
                if (apiLogs.length === 0) {
                  return <Empty description={t('common.table.no_data')} />;
                }
                return (
                  <List
                    dataSource={apiLogs}
                    pagination={{ pageSize: 10 }}
                    size="small"
                    renderItem={(log: any) => (
                      <List.Item style={{ display: 'block', padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                          <Tag>{log.request_method}</Tag>
                          <Tag color={
                            log.response_http_status >= 200 && log.response_http_status < 300 ? 'success' :
                              log.response_http_status >= 400 && log.response_http_status < 500 ? 'warning' :
                                log.response_http_status >= 500 ? 'error' : 'default'
                          }>{log.response_http_status}</Tag>
                          <span style={{ fontSize: '12px', color: '#666', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.request_path}</span>
                          <span style={{ fontSize: '12px', color: '#999', whiteSpace: 'nowrap' }}>{formatDateTime(log.created_at)}</span>
                        </div>
                        <Collapse size="small" items={[
                          {
                            key: 'detail',
                            label: <span style={{ fontSize: '12px' }}>{t('shared.orders.detail.logs.request')} / {t('shared.orders.detail.logs.response')}</span>,
                            children: (
                              <div style={{ display: 'flex', gap: '12px' }}>
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontSize: '11px', color: '#999', marginBottom: '4px' }}>{t('shared.orders.detail.logs.request')}</div>
                                  <pre style={{ background: '#f5f5f5', padding: '8px', borderRadius: '4px', margin: 0, fontSize: '11px', overflow: 'auto', maxHeight: '200px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{log.request || '-'}</pre>
                                </div>
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontSize: '11px', color: '#999', marginBottom: '4px' }}>{t('shared.orders.detail.logs.response')}</div>
                                  <pre style={{ background: '#f5f5f5', padding: '8px', borderRadius: '4px', margin: 0, fontSize: '11px', overflow: 'auto', maxHeight: '200px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{log.response || '-'}</pre>
                                </div>
                              </div>
                            ),
                          }
                        ]} />
                      </List.Item>
                    )}
                  />
                );
              })(),
            },
          ]}
        />
      </Card>
      {/* 預期計畫 (Inbound Items) */}
      <Card
        title={t(`models.inbound.tabs.items`, { defaultValue: '預期計畫 (Items)' })}
        size="small"
        style={{ marginBottom: 16 }}
        styles={{ body: { padding: 0 } }}
      >
        <Table
          columns={itemColumns}
          dataSource={inbound.items || []}
          rowKey="id"
          pagination={false}
          size="small"
        />
      </Card>

      {/* 收貨明細 (Receipts) */}
      <Card
        title={t(`models.inbound.tabs.receipts`)}
        size="small"
        style={{ marginBottom: 16 }}
      >
        {inbound.receipts && inbound.receipts.length > 0 ? (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            {inbound.receipts.map((receipt: any) => {
              const lastPutawayError = receipt.auto_putaway_available && inbound.histories
                ?.filter((h: any) => h.description?.key === 'auto_putaway_failed' && h.description?.receipt_name === receipt.name)
                ?.sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
                ?.[0];
              return (
              <Card
                key={receipt.id}
                type="inner"
                title={
                  <Space>
                    <span>{receipt.name}</span>
                    <Tag color={receiptStatusColors[receipt.status] || 'default'}>
                      {t(`enums.receipt.status.${receipt.status}`, { defaultValue: receipt.status })}
                    </Tag>
                    {lastPutawayError && (
                      <Tooltip title={
                        lastPutawayError.description?.error_code
                          ? t([`enums.inbound_history.auto_putaway_failed.${lastPutawayError.description.error_code}`, `errors.${lastPutawayError.description.error_code}`], { ...lastPutawayError.description.interpolations, defaultValue: lastPutawayError.description.error_code })
                          : lastPutawayError.description?.reason || lastPutawayError.description?.error
                      }>
                        <Tag color="error" icon={<WarningOutlined />}>
                          {t('operator.pages.receipts.auto_putaway_failed')}
                        </Tag>
                      </Tooltip>
                    )}
                    <span style={{ fontSize: 13, color: '#888', fontWeight: 'normal' }}>
                      {t('models.receipt.received_at', { defaultValue: '驗收完成時間' })}: {receipt.received_at ? formatDateTime(receipt.received_at) : '-'}
                    </span>
                  </Space>
                }
                extra={
                  receipt.auto_putaway_available && (
                    <CanShow requiredPermission="inbounds:write">
                      <Button
                        size="small"
                        type="primary"
                        ghost
                        onClick={() => {
                          Modal.confirm({
                            title: t('operator.pages.receipts.auto_putaway_confirm'),
                            okText: t('common.button.confirm'),
                            cancelText: t('common.button.cancel'),
                            onOk: () => handleAutoPutaway(receipt.id),
                          });
                        }}
                      >
                        {t('operator.pages.receipts.auto_putaway')}
                      </Button>
                    </CanShow>
                  )
                }
                styles={{ body: { padding: 0 } }}
              >
                <Table
                  columns={receiptItemWithProductColumns}
                  dataSource={receipt.receipt_items || []}
                  rowKey="id"
                  pagination={false}
                  size="small"
                />
              </Card>
              );
            })}
          </Space>
        ) : (
          <Empty description={t('common.table.no_data')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
        )}
      </Card>

      {/* Value Added Services - 計費相關，不受 inbound 狀態限制 */}
      {role === 'operator' ? (
        <ValueAddedServicesCard
          record={inbound}
          vasService={{
            add: (recordId: string, data: any) => service.addValueAddedService(recordId, data),
            update: (recordId: string, serviceId: string, data: any) => service.updateValueAddedService(recordId, serviceId, data),
            delete: (recordId: string, serviceId: string) => service.deleteValueAddedService(recordId, serviceId),
          }}
          valueAddedServiceService={valueAddedServiceService}
          onUpdate={() => revalidator.revalidate()}
          editable={true}
        />
      ) : (
        inbound.value_added_services && inbound.value_added_services.length > 0 && (
          <Card
            title={t('models.merchant_value_added_service._plural')}
            size="small"
            styles={{ body: { padding: 0 } }}
          >
            <Table
              columns={valueAddedServiceColumns}
              dataSource={inbound.value_added_services}
              rowKey="id"
              pagination={false}
              size="small"
              locale={{ emptyText: t('common.table.no_data') }}
            />
          </Card>
        )
      )}

      {/* 次要資訊 Tabs */}
      <Tabs items={secondaryTabItems} destroyInactiveTabPane />

      {/* 選擇要上架的收貨單 */}
      <Modal
        open={putawayPickerOpen}
        onCancel={() => setPutawayPickerOpen(false)}
        footer={null}
        title={t('operator.pages.receipts.select_receipt_for_putaway')}
      >
        <List
          dataSource={putawayReceipts}
          renderItem={(receipt: any) => {
            const totalQty = (receipt.receipt_items || []).reduce((s: number, ri: any) => s + (ri.quantity || 0), 0);
            const putawayQty = (receipt.receipt_items || []).reduce((s: number, ri: any) => s + (ri.putaway_quantity || 0), 0);
            return (
              <List.Item
                onClick={() => {
                  setPutawayPickerOpen(false);
                  navigate(`/receipts/${receipt.id}/putaway`);
                }}
                style={{ cursor: 'pointer' }}
              >
                <List.Item.Meta
                  title={receipt.name}
                  description={
                    <Space>
                      <Tag>{t(`enums.receipt.status.${receipt.status}`, { defaultValue: receipt.status })}</Tag>
                      <span>{t('operator.pages.putaway.header.progress')}: {putawayQty}/{totalQty}</span>
                    </Space>
                  }
                />
              </List.Item>
            );
          }}
        />
      </Modal>
    </div>
  );
};

export default InboundShow;
